import React from "react";
import isEmpty from "lodash/isEmpty";
import { Route, Switch, Link } from "react-router-dom";

import { JobViewProvider } from "@ats/src/context/JobViewContext";
import WebsocketJobChannelHandler from "@ats/src/websockets/WebsocketJobChannelHandler";

import Banner from "@ats/src/components/shared/Banner";
import NavBar from "@shared/components/NavBar";
import LoadingIndicator from "@ats/src/components/shared/LoadingIndicator";
import NewCandidateModal from "@ats/src/views/jobApplications/candidates/NewCandidateModal";

import JobStagesContainer from "@ats/src/views/jobApplications/JobStagesContainer";
import JobStripeCheckoutRedirectHandler from "@ats/src/views/jobApplications/JobStripeCheckoutRedirectHandler";

import { useModalContext } from "@shared/context/ModalContext";
import { useCurrentSession } from "@ats/src/context/CurrentSessionContext";
import { useJob } from "@shared/queryHooks/useJob";
import { useCreateJobCandidatesCsvExport } from "@shared/queryHooks/useCsvExport";
import { useCreateJobResumeExport } from "@shared/queryHooks/useJobResumeExport";
import { useToastContext } from "@shared/context/ToastContext";
import { useFeatureGate } from "@shared/hooks/useFeatureGate";
import { PlanFeatures } from "@shared/types/organization";
import PlanBadge from "@ats/src/components/shared/PlanBadge";
import { trackEvent } from "@shared/lib/posthog";
import { useJobStore } from "@ats/src/lib/store/zustand/jobStore";
import DropdownMenu from "@ats/src/components/shared/DropdownMenu";
import FriendlyErrors from "@ats/src/components/shared/FriendlyErrors";
import JobImportCandidatesModal from "@ats/src/components/modals/JobImportCandidatesModal";
import { useFeatureFlipper, Features } from "@ats/src/components/shared/FeatureFlipper";
import { hiringStagesForJobOptions } from "@ats/src/lib/utils/helpers";
import { isDeadlineDateAfterCurrent } from "@shared/lib/time";
// import { useWhyDidYouUpdate } from "@shared/hooks/useWhyDidYouUpdate";

function JobContainer(props: any) {
  // useWhyDidYouUpdate("JobContainer ", props);
  // window.logger("%c[JobContainer] ", "background: #EFDDEF; color: #1976D2", props);
  const { match, history, location } = props;
  const memoMatch = React.useMemo(() => match, [match]);
  const { currentOrganization, currentOrganizationUser } = useCurrentSession();
  const webflowUpdatesInfoBannerEnabled = useFeatureFlipper()({
    feature: Features.WEBFLOW_UPDATE_ALERTS,
  });
  const hadAnActiveSubscriptionInThePast =
    !currentOrganization.stripeSubscriptionInGoodStanding &&
    currentOrganization.publishedJobsCount > 0;
  const jobId = Number(match.params.jobId);
  const shouldDisplayWebflowUpdatesBanner =
    webflowUpdatesInfoBannerEnabled &&
    currentOrganization?.stripeSubscriptionInGoodStanding &&
    currentOrganization?.hasWebflowAuthentication &&
    !currentOrganization?.webflowAuthenticatedViaApiV2;

  const { data: jobData, refetch: refetchJob, isFetching: isFetchingJob, error } = useJob({
    jobId,
    refetchOnWindowFocus: false,
  });

  const {
    mutate: createJobCandidatesCsvExport,
    error: errorOnCreateCandidatesCsvExport,
    isLoading: isLoadingCreateCandidatesCsvExport,
  } = useCreateJobCandidatesCsvExport();

  const addToast = useToastContext();
  const { getFeatureInfo } = useFeatureGate();
  const bulkResumeExportFeature = getFeatureInfo(PlanFeatures.BULK_RESUME_EXPORT);

  const {
    mutate: createJobResumeExport,
    isLoading: isLoadingCreateJobResumeExport,
  } = useCreateJobResumeExport();

  const setJob = useJobStore(React.useCallback((state) => state.set, []));
  const { openModal, removeModal } = useModalContext();
  const [orgAdminJobsListUrl, setOrgAdminJobsListUrl] = React.useState("");

  React.useEffect(() => {
    // setOrgAdminJobsListUrl once on initial component rende
    if (currentOrganizationUser.isAdmin) {
      setOrgAdminJobsListUrl(location.state?.orgAdminJobsListUrl);
    }
  }, []);

  const updateJobStore = React.useCallback(() => {
    // window.logger("%c[JobContainer] UPDATE JOB STORE", "background: #FF002F; color: #F9FFFF", {
    //   jobData,
    //   setJob,
    // });
    setJob((state) => {
      state.jobsById[jobData.id] = jobData;
      state.questionsByJobId[jobData.id] = jobData.questions;
      state.hiringStagesByJobId[jobData.id] = jobData.hiringStages;

      jobData.questions.forEach((question) => {
        if (question != undefined) {
          state.questionsById[question.id] = question;
        }
      });
      jobData.hiringStages.forEach((hiringStage) => {
        if (hiringStage != undefined) {
          state.hiringStagesById[hiringStage.id] = hiringStage;
        }
      });
    });
  }, [jobData, setJob]);

  if (jobData != undefined) {
    updateJobStore();
  }

  React.useEffect(() => {
    // SAVE JOB
    if (jobData != undefined) {
      updateJobStore();
    }
  }, [jobData, jobId, updateJobStore]);

  React.useEffect(() => {
    // window.logger("%c[JobContainer] useEffect refetchJob", "background: #FF002F; color: #F9FFFF", {
    //   jobId,
    // });
    // Get the Job
    refetchJob();
  }, [jobId, refetchJob]);

  const job = useJobStore(React.useCallback((state) => state.jobsById[jobId], [jobId])) as any;
  const jobTitle = job && job.title ? job.title : "";
  const backUrl = orgAdminJobsListUrl ? orgAdminJobsListUrl : "/jobs";

  if (error) {
    return (
      <FriendlyErrors
        statusCode={error.response.status}
        resource="job"
        role={currentOrganizationUser?.role}
      />
    );
  }

  const inboxStage = jobData && jobData.hiringStages.find((stage) => stage.kind === "kind_inbox");

  // window.logger("%c[JobContainer] render", "background: #f199f1; color: #1900D2", {
  //   inboxStage,
  //   jobData,
  //   job,
  //   jobId: job?.id,
  //   matchSAME: match === memoMatch,
  // });

  const exportCandidatesToCsv = (event) => {
    event.preventDefault();
    trackEvent("candidates_export_clicked", { job_id: jobId });

    createJobCandidatesCsvExport({ jobId });
  };

  const exportResumes = (event) => {
    event.preventDefault();
    trackEvent("resumes_export_clicked", { job_id: jobId });

    createJobResumeExport(
      { jobId },
      {
        onSuccess: (response: any) => {
          if (response?.downloadUrl) {
            // Cache-hit branch — server returned an existing fresh export URL inline.
            addToast({
              kind: "success",
              title: response.message,
              externalLink: { href: response.downloadUrl, text: "Download" },
              delay: 10000,
            });
          } else {
            // Fresh kickoff — BG job will email + broadcast on completion.
            addToast({
              kind: "success",
              title: response.message,
              delay: 10000,
            });
          }
        },
        onError: (error: any) => {
          // Status code mapping: 402 = plan gate, 409 = in-progress, 422 = empty job or over-limit.
          // apiMutate (api.ts:54-63) rejects with { ...response, data: responseData }, so error.status / error.data.
          const status = error?.status;
          const serverMessage = error?.data?.errors?.general?.[0];

          if (status === 402) {
            addToast({
              kind: "warning",
              title: "Bulk Resume Export requires a paid plan.",
              linkTo: "/hire/settings/billing",
              delay: 20000,
            });
          } else if (status === 409) {
            addToast({ kind: "warning", title: serverMessage, delay: 10000 });
          } else if (status === 422) {
            // Both empty-job and over-APPLICANT_LIMIT render as 422 with the server's message.
            addToast({ kind: "info", title: serverMessage, delay: 10000 });
          } else {
            // Network error / 500 / unexpected shape — no server message to show.
            addToast({
              kind: "error",
              title: "Unable to start resume export. Please try again.",
              delay: 10000,
            });
          }
        },
      },
    );
  };

  const importCandidatesFromCsv = () => {
    const hiringStageOptions = hiringStagesForJobOptions(job);
    const modal = (
      <JobImportCandidatesModal
        onCancel={removeModal}
        jobId={jobId}
        hiringStageOptions={hiringStageOptions}
      />
    );

    openModal(modal);
  };

  const jobActions = () => {
    const bulkResumeExportPlanBadge = bulkResumeExportFeature.upgradePlanKey ? (
      <PlanBadge
        planKey={bulkResumeExportFeature.upgradePlanKey}
        planDisplayName={bulkResumeExportFeature.planDisplayName}
        size="small"
        locked={bulkResumeExportFeature.isLocked}
        includeTag={false}
      />
    ) : null;

    return (
      <DropdownMenu testid="job-candidates-menu" label="Job options">
        <button onClick={handleClickOnAdd}>Add new candidate</button>
        <button onClick={importCandidatesFromCsv}>Import candidates</button>
        <button onClick={exportCandidatesToCsv}>Export candidates</button>
        <button onClick={exportResumes} disabled={isLoadingCreateJobResumeExport}>
          Export resumes
          {bulkResumeExportPlanBadge}
        </button>
      </DropdownMenu>
    );
  };

  const onCompleteAddingCandidate = (jobApplication) => {
    if (jobApplication) {
      history.push(`/jobs/${jobId}/stages/${inboxStage.id}/applicants/${jobApplication.id}`);
      return removeModal; // Must return uncalled "removeModal" function in order to allow this function to be called after the animation to remove the component completes.
    }
  };

  /* ADD NEW CANDIDATE
  --===================================================-- */
  const handleClickOnAdd = (e) => {
    e.preventDefault();
    // window.logger("%c[JobContainer] handleClickOnAdd props", "color: #1976D2", props);

    const modal = (
      <NewCandidateModal
        jobId={job.id}
        history={history}
        match={match}
        onCancel={removeModal}
        onComplete={onCompleteAddingCandidate}
      />
    );

    openModal(modal);
  };

  const getWebflowBannerMessage = () => {
    const isAfterDeadline = isDeadlineDateAfterCurrent("03-17-2025");
    const bannerLinkToWebflow = {
      pathname: `/hire/settings/integrations/webflow`,
      state: { orginalPathname: location.pathname },
    };
    let message;
    if (isAfterDeadline) {
      message = (
        <span>
          <b>Your jobs are not syncing with Webflow.</b> To resume syncing, reauthenticate your
          Webflow account in{" "}
          {
            <Link to={bannerLinkToWebflow}>
              <b>integration settings.</b>
            </Link>
          }
        </span>
      );
    } else {
      message = (
        <span>
          Webflow integrations must be reauthenticated by <b>March 17, 2025</b> or job data will no
          longer sync. Visit{" "}
          {
            <Link to={bannerLinkToWebflow}>
              <b>integration settings</b>
            </Link>
          }{" "}
          to reauthenticate.
        </span>
      );
    }
    return message;
  };

  const getBanner = () => {
    const bannerLinkToContent = {
      pathname: `/hire/settings/billing`,
      state: { orginalPathname: location.pathname },
    };

    if (job?.status === "status_archived") {
      return <Banner message="This job has been archived" />;
    } else if (job?.status === "draft") {
      return <Banner message="This job is not published" />;
    } else if (job?.status === "published" && job.visible === false) {
      return <Banner message="This job post is currently hidden on your public job board" />;
    } else if (hadAnActiveSubscriptionInThePast) {
      return (
        <Banner
          message={
            <span>
              Published job posts are not visible without an active subscription. Visit{" "}
              {<Link to={bannerLinkToContent}>plan & billing</Link>} to find out more.
            </span>
          }
        />
      );
    } else if (shouldDisplayWebflowUpdatesBanner) {
      return <Banner message={getWebflowBannerMessage()} />;
    }
  };

  return location.pathname.includes("stripe_checkout_redirect_handler") ? (
    <Route
      exact
      path="/jobs/:jobId/stripe_checkout_redirect_handler"
      render={(renderProps) => (
        <JobStripeCheckoutRedirectHandler {...props} {...renderProps} job={job} />
      )}
    />
  ) : (
    <JobViewProvider>
      <WebsocketJobChannelHandler jobId={jobId}>
        {getBanner()}
        <NavBar
          isBack={true}
          backUrl={backUrl}
          title={jobTitle}
          titleUrl={`/jobs/${jobId}/stages`}
          history={history}
          secondary={jobActions()}
        />
        {isEmpty(job) && isFetchingJob ? (
          // ||
          // (isEmpty(jobApplications) && isFetchingJobApplications && job.status != "draft")
          <LoadingIndicator label="Loading..." />
        ) : (
          <Switch>
            <Route
              path="/jobs/:jobId"
              render={(renderProps) => (
                <JobStagesContainer
                  {...props}
                  {...renderProps}
                  job={job}
                  refetchJob={refetchJob}
                  match={memoMatch}
                  orgAdminJobsListUrl={orgAdminJobsListUrl} //to allow for use in nested & linked components
                />
              )}
            />
          </Switch>
        )}
      </WebsocketJobChannelHandler>
    </JobViewProvider>
  );
}

export default JobContainer;
